import { CdkTrapFocus } from '@angular/cdk/a11y';
import { CdkConnectedOverlay, CdkOverlayOrigin, ScrollStrategyOptions, ConnectedPosition } from '@angular/cdk/overlay';
import { CommonModule, NgClass } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  model,
  numberAttribute,
  OnDestroy,
  QueryList,
  signal,
  ViewChild,
  ViewChildren,
  ViewEncapsulation
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { TranslateModule } from '@ngx-translate/core';
import { ArrowDownIcon } from '../../../../assets/icons/arrow-down/arrow-down';
import { ShebaButtonComponent } from '../../../shared/components/button/button.component';
import { MultiselectOption } from './models/multiselect-option.model';

// Reactive forms does not support signals, so we do not use them
@Component({
  selector: 'sheba-multi-select',
  standalone: true,
  imports: [
    CdkOverlayOrigin,
    TranslateModule,
    NgClass,
    ArrowDownIcon,
    CdkConnectedOverlay,
    MatCheckbox,
    ShebaButtonComponent,
    CdkTrapFocus,
    CommonModule,
    FormsModule
  ],
  templateUrl: './multi-select.component.html',
  styleUrl: './multi-select.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MultiSelectComponent implements AfterViewInit, OnDestroy {
  @ViewChildren(MatCheckbox, { read: ElementRef }) checkboxes!: QueryList<ElementRef>;
  @ViewChild('clearButton', { read: ElementRef }) clearButton!: ElementRef;
  @ViewChild('origin', { read: ElementRef }) origin!: ElementRef;
  @ViewChild(CdkConnectedOverlay) overlayDir!: CdkConnectedOverlay;

  readonly filterTitle = input<string>('');
  readonly options = input<MultiselectOption[]>([]);
  readonly buttonCustomClass = input<string>();
  readonly popupCustomClass = input<string>();
  readonly displayFilterByTitle = input<boolean>(true);
  readonly matchButtonWidth = input<boolean>(false);
  readonly showFilterTitleOnly = input<boolean>(false);
  readonly selected = model<MultiselectOption[]>([]);

  readonly maxSelectLimit = input(10, { transform: numberAttribute });
  
  readonly scrollStrategy = inject(ScrollStrategyOptions).noop();
  
  readonly isOpen = signal(false);
  readonly buttonWidth = signal<number | undefined>(undefined);
  focusedIndex = signal(-1);

  readonly overlayPositions: ConnectedPosition[] = [
    {
      originX: 'center',
      originY: 'bottom',
      overlayX: 'center',
      overlayY: 'top',
      offsetY: 8
    },
    {
      originX: 'center',
      originY: 'top',
      overlayX: 'center',
      overlayY: 'bottom',
      offsetY: -8
    }
  ];

  readonly isMaxSelected = computed(() => this.selected().length >= this.maxSelectLimit());
  readonly placeholder = computed(() => {
    if (this.showFilterTitleOnly()) {
      return this.filterTitle();
    }
    return this.selected().length
      ? this.selected()
        .map((v) => v.name)
        .join(', ')
      : this.filterTitle();
  });
  readonly overlayWidth = computed<number | string>(() => {
    const width = this.buttonWidth();
    return this.matchButtonWidth() && width !== undefined ? width : 'auto';
  });

  private keydownHandler = (event: KeyboardEvent) => {
    if (!this.isOpen()) return;

    const active = document.activeElement;
    const originEl = this.origin.nativeElement;

    if (event.key === 'Tab') {
      if (active === originEl) {
        event.preventDefault();
        this.clearButton.nativeElement.focus();
      } else {
        setTimeout(() => this.focusedIndex.set(this.getFocusedCheckboxIndex()), 0);
      }
    }

    if (event.key === 'Enter') {
      const index = this.focusedIndex();
      if (index >= 0) {
        const option = this.options()[index];
        this.onSelectChange(!this.isSelected(option), option);
      }
    }
  };

  private wheelOrTouchMoveHandler = (event: Event) => {
    if (!this.isOpen()) return;

    const target = event.target as HTMLElement;
    const overlayElement = this.overlayDir?.overlayRef?.overlayElement;

    if (overlayElement && target && overlayElement.contains(target)) {
      return;
    }

    this.onCloseMenu();
  };

  ngAfterViewInit() {
    document.addEventListener('keydown', this.keydownHandler);
    window.addEventListener('wheel', this.wheelOrTouchMoveHandler, { passive: true });
    window.addEventListener('touchmove', this.wheelOrTouchMoveHandler, { passive: true });
  }

  getFocusedCheckboxIndex(): number {
    const active = document.activeElement;
    return this.checkboxes.toArray().findIndex(chk =>
      chk.nativeElement === active || chk.nativeElement.contains(active)
    );
  }

  isSelected(option: MultiselectOption): boolean {
    return this.selected().some((sel) => sel.id === option.id);
  }

  onSelectChange(value: boolean, option: MultiselectOption) {
    if (value) {
      if (this.isMaxSelected()) {
        return;
      }
      this.selected.set([...this.selected(), option]);
    } else {
      this.selected.set(this.selected().filter((sel) => sel.id !== option.id));
    }
  }

  clearSelection() {
    this.selected.set([]);
  }

  onCloseMenu() {
    this.isOpen.set(false);
    setTimeout(() => this.origin.nativeElement.focus(), 0);
  }

  openCloseOverlay() {
    if (!this.isOpen() && this.matchButtonWidth() && this.origin?.nativeElement) {
      this.buttonWidth.set(this.origin.nativeElement.offsetWidth);
    }
    this.isOpen.set(!this.isOpen());
  }

  ngOnDestroy() {
    document.removeEventListener('keydown', this.keydownHandler);
    window.removeEventListener('wheel', this.wheelOrTouchMoveHandler);
    window.removeEventListener('touchmove', this.wheelOrTouchMoveHandler);
  }
}
